/**
 * IndexedDB Connection Manager
 * Provides a singleton pattern for database connections to prevent interrupting active transactions
 */

const DB_NAME = 'L1N'
const DB_VERSION = 1

// Connection state tracking
let dbInstance = null
let connectionPromise = null
let isConnecting = false
let connectionError = null

/**
 * Get database connection using a true singleton pattern
 * @returns {Promise<IDBDatabase>} Database connection
 */
export const getConnection = () => {
    // Return existing connection if available and healthy
    if (dbInstance && dbInstance.transaction) {
        return Promise.resolve(dbInstance)
    }

    // Return existing connection promise if in progress
    if (isConnecting && connectionPromise) {
        return connectionPromise
    }

    // Reset connection state for new connection
    isConnecting = true
    connectionError = null

    // Create new connection
    connectionPromise = new Promise((resolve, reject) => {
        try {
            console.log('Opening IndexedDB connection to', DB_NAME)
            const request = indexedDB.open(DB_NAME, DB_VERSION)

            request.onupgradeneeded = event => {
                console.log('Upgrading IndexedDB schema to version', DB_VERSION)
                const db = event.target.result

                // Create stores if they don't exist
                if (!db.objectStoreNames.contains('dashboard')) {
                    db.createObjectStore('dashboard', { keyPath: 'id' })
                }
                if (!db.objectStoreNames.contains('fleets')) {
                    db.createObjectStore('fleets', { keyPath: 'id' })
                }
                if (!db.objectStoreNames.contains('requests')) {
                    db.createObjectStore('requests', { keyPath: 'id' })
                }
                if (!db.objectStoreNames.contains('pmWorkOrders')) {
                    db.createObjectStore('pmWorkOrders', { keyPath: 'id' })
                }
                if (!db.objectStoreNames.contains('pendingRequests')) {
                    db.createObjectStore('pendingRequests', { keyPath: 'id' })
                }
                if (!db.objectStoreNames.contains('reviewRequests')) {
                    db.createObjectStore('reviewRequests', { keyPath: 'id' })
                }
                if (!db.objectStoreNames.contains('requestsDetails')) {
                    db.createObjectStore('requestsDetails', { keyPath: 'id' })
                }
                if (!db.objectStoreNames.contains('pendingEvents')) {
                    db.createObjectStore('pendingEvents', { keyPath: 'id' })
                }
            }

            request.onsuccess = event => {
                console.log('IndexedDB connection established successfully')
                dbInstance = event.target.result

                // Listen for connection close
                dbInstance.onclose = () => {
                    console.log('IndexedDB connection closed')
                    dbInstance = null
                }

                // Listen for version change (another tab/window opened the db with a different version)
                dbInstance.onversionchange = () => {
                    if (dbInstance) {
                        dbInstance.close()
                        dbInstance = null
                        console.log('IndexedDB closed due to version change')
                    }
                }

                isConnecting = false
                resolve(dbInstance)
            }

            request.onerror = event => {
                console.error('Database connection error:', event.target.error)
                isConnecting = false
                connectionError = event.target.error
                reject(event.target.error)
            }
        } catch (error) {
            console.error('Unexpected error opening IndexedDB:', error)
            isConnecting = false
            connectionError = error
            reject(error)
        }
    })

    return connectionPromise
}

/**
 * Check connection health
 * @returns {boolean} True if connection is healthy
 */
export const isConnectionHealthy = () => {
    return Boolean(dbInstance && dbInstance.transaction)
}

/**
 * Get the last connection error
 * @returns {Error|null} The last connection error or null
 */
export const getConnectionError = () => {
    return connectionError
}

/**
 * Explicitly closes the database connection - use with caution!
 * Only call this when you're certain no transactions are in progress
 */
export const closeConnection = () => {
    if (dbInstance) {
        try {
            dbInstance.close()
            console.log('IndexedDB connection closed explicitly')
        } catch (error) {
            console.error('Error closing IndexedDB connection:', error)
        } finally {
            dbInstance = null
        }
    }
}
